import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';

import { formValueSelector } from 'redux-form';
import { SaleTransactionForm } from '../../../../constants/constant_form_names';
import { getParticularPreferenceSettings } from '../../../../common/selectors/preference';

const selector = formValueSelector(SaleTransactionForm);

const getFormReferenceId = (state) => selector(state, 'reference_id');

const getFormTransactionType = (state) => selector(state, 'type');

const getFormTransactionStatus = (state) => selector(state, 'status');

const getFormTransactions = (state) => selector(state, 'transactions');

const getFormCurrency = (state) => selector(state, 'currency') || {};

const getFormItemLines = (state) => selector(state, 'item_lines') || [];

const getFormCustomTabs = (state) => selector(state, 'custom_tabs') || [];

const getFormTaxBreakdown = (state) => selector(state, 'tax_breakdown') || [];

const getFormInventoryLines = (state) =>
  selector(state, 'inventory_adjustments', 'inventory_transfers') || {};

const getFormItemLine = (state, index) => {
  return selector(state, `item_lines[${index}]`) || {};
};

export const getReferenceId = createSelector(
  [getFormReferenceId],
  (referenceId) => {
    return referenceId;
  },
);

export const getTransactionType = createSelector(
  [getFormTransactionType],
  (type_history) => {
    return type_history;
  },
);

export const getTransactionStatus = createSelector(
  [getFormTransactionStatus],
  (status) => {
    return status && status.name;
  },
);

export const isClosed = createSelector([getFormTransactionStatus], (status) => {
  return status && status.cid === 'dt:transaction_status:closed';
});

export const isOpen = createSelector([getFormTransactionStatus], (status) => {
  return status && status.cid === 'dt:transaction_status:open';
});

export const getTransactions = createSelector(
  [getFormTransactions],
  (transactions) => {
    return transactions;
  },
);

export const getCurrencyCode = createSelector([getFormCurrency], (currency) => {
  return currency.code || 'USD';
});

export const getCurrencySymbol = createSelector(
  [getFormCurrency],
  (currency) => {
    return currency.symbol || '$';
  },
);

export const getCustomTabs = createSelector(
  [getFormCustomTabs],
  (customTabs) => customTabs,
);

export const getItemLineCustomTabs = createSelector(
  [getFormItemLine],
  ({ custom_tabs }) => custom_tabs || [],
);

export const getItemLineLinkStatus = createSelector(
  [getFormItemLine],
  ({ linked_item }) => linked_item,
);

export const getSubTotal = createSelector([getFormItemLines], (itemLines) => {
  return itemLines.reduce((subTotal, itemLine) => {
    const { total_ex_tax } = itemLine;
    return subTotal + total_ex_tax;
  }, 0);
});

export const getTotal = createSelector([getFormItemLines], (itemLines) => {
  return itemLines.reduce((totalAmount, itemLine) => {
    const { total } = itemLine;
    return total + totalAmount;
  }, 0);
});

export const getTotalTax = createSelector([getFormItemLines], (itemLines) => {
  return itemLines.reduce((totalTax, itemLine) => {
    const { total_ex_tax, total } = itemLine;
    const tax = total - total_ex_tax;
    return totalTax + tax;
  }, 0);
});

export const getInventoryLines = createSelector(
  [getFormInventoryLines],
  ({ inventory_adjustments = [], inventory_transfers = [] }) => {
    const inventoryLines = [...inventory_adjustments, ...inventory_transfers];
    return inventoryLines.sort((a, b) => {
      return (
        new Date(b.created_datetime) - new Date(a.created_datetime) ||
        new Date(b.processed_date) - new Date(a.processed_date) ||
        b.reference_id.localeCompare(a.reference_id)
      );
    });
  },
);

export const getInventoryPagination = createSelector(
  [
    (state) => (state.sale_transaction || {}).inventory,
    (state) => {
      const { value } = getParticularPreferenceSettings(
        state,
        'search_default_rows_count_list',
        ['value'],
      );
      return Number(value);
    },
    (state, useDefault) => useDefault,
  ],
  (data, defaultRowsPerPage, useDefault) => {
    const pagination = {
      requested_page: useDefault ? 1 : data.requested_page,
      rows_per_page: useDefault
        ? defaultRowsPerPage
        : data.rows_per_page || defaultRowsPerPage,
      total_rows: data.total_rows,
      page: data.page,
      is_fetching: data.is_fetching,
    };
    return pagination;
  },
);

export const getReturnLines = createSelector(
  [getFormItemLines],
  (itemLines) => {
    // Obtain all the transactions for returns
    return itemLines.reduce((returnLines, line) => {
      const newRetLines = [...returnLines];

      // If there is a reference to a parent sale, append
      line.return_ref &&
        line.return_ref.ref &&
        newRetLines.push(line.return_ref.ref);

      // Append all returned lines (if any)
      const returnReferences = (line.was_returned || []).map(
        (returned) => returned.return_reference,
      );
      newRetLines.push(...returnReferences);

      return newRetLines;
    }, []);
  },
);

export const getTaxBreakdown = createSelector(
  [getFormTaxBreakdown],
  (taxBreakdown) =>
    taxBreakdown.map((b) => {
      const {
        total,
        tax_group: { name: taxGroup },
        tax_type: { name: taxType },
      } = b;

      return {
        total,
        taxGroup,
        taxType,
      };
    }),
);

export const hasTaxBreakdown = createSelector(
  [getTaxBreakdown],
  (taxBreakdown) => !isEmpty(taxBreakdown),
);

export const getUomRetailPriceExTax = createSelector(
  [(state, line) => selector(state, line)],
  (line) => {
    const { item_uom, retail_price, uom_retail_price, tax_rate } = line;
    if (item_uom) {
      const { conversion_rate } = item_uom;
      const retailPriceExTax =
        uom_retail_price / conversion_rate / (1 + Number(tax_rate));
      return retailPriceExTax * conversion_rate;
    }
    return retail_price;
  },
);

export const hasItemUom = createSelector(
  [(state, line) => selector(state, line)],
  (line) => {
    const { item_uom } = line;
    return Boolean(item_uom);
  },
);
